import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Progress } from "../components/ui/progress";
import { Banknote, Plus, Pencil, Trash2, AlertTriangle, TrendingUp, Activity } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COST_KINDS = [
  { value: "labor", label: "Labor" },
  { value: "material", label: "Material" },
  { value: "expense", label: "Expense" },
  { value: "other", label: "Other" },
];

const blankBudget = (sch_id = "") => ({
  schedule_id: sch_id, labor_budget: 0, materials_budget: 0, other_budget: 0,
  contingency_pct: 0, alert_thresholds_pct: [50, 80, 100],
  notify_admin: true, notify_pm: false, notes: "",
});

const blankCost = (sch_id) => ({
  schedule_id: sch_id, kind: "labor", amount: 0, description: "", source_ref: null,
});

export default function JobBudgets() {
  const [budgets, setBudgets] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [summary, setSummary] = useState(null);
  const [budgetDialog, setBudgetDialog] = useState(null);
  const [activeBudget, setActiveBudget] = useState(null);
  const [costEntries, setCostEntries] = useState([]);
  const [costDialog, setCostDialog] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const [b, s, sch] = await Promise.all([
        axios.get(`${API}/job-budgets`),
        axios.get(`${API}/job-budgets/summary`),
        axios.get(`${API}/schedules`).catch(() => ({ data: [] })),
      ]);
      setBudgets(b.data || []);
      setSummary(s.data);
      setSchedules(sch.data?.items || sch.data || []);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === "over") return budgets.filter((b) => b.status?.is_over);
    if (filter === "approaching") return budgets.filter((b) => !b.status?.is_over && b.status?.pct_used >= 80);
    if (filter === "healthy") return budgets.filter((b) => b.status?.pct_used < 80);
    return budgets;
  }, [budgets, filter]);

  const openBudget = async (b) => {
    setActiveBudget(b);
    try {
      const r = await axios.get(`${API}/job-budgets/${b.id}/cost-entries`);
      setCostEntries(r.data || []);
    } catch { toast.error("Cost entries load failed"); }
  };

  const saveBudget = async () => {
    if (!budgetDialog?.schedule_id) { toast.error("Pick a job"); return; }
    try {
      if (budgetDialog.id) await axios.put(`${API}/job-budgets/${budgetDialog.id}`, budgetDialog);
      else await axios.post(`${API}/job-budgets`, budgetDialog);
      toast.success("Budget saved"); setBudgetDialog(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const deleteBudget = async (id) => {
    if (!window.confirm("Delete budget + threshold history?")) return;
    try { await axios.delete(`${API}/job-budgets/${id}`); load(); if (activeBudget?.id === id) { setActiveBudget(null); setCostEntries([]); } }
    catch { toast.error("Delete failed"); }
  };

  const saveCost = async () => {
    try {
      await axios.post(`${API}/job-budgets/cost-entries`, costDialog);
      toast.success("Cost logged");
      setCostDialog(null);
      load();
      if (activeBudget) openBudget(activeBudget);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const deleteCost = async (id) => {
    if (!window.confirm("Delete cost entry?")) return;
    try { await axios.delete(`${API}/job-budgets/cost-entries/${id}`); openBudget(activeBudget); load(); }
    catch { toast.error("Delete failed"); }
  };

  const schedTitle = (id) => {
    const s = schedules.find((x) => x.id === id);
    return s ? s.title : "(deleted job)";
  };

  const statusBadge = (status) => {
    if (!status) return null;
    if (status.is_over) return <Badge variant="destructive"><AlertTriangle className="h-3 w-3 mr-1" />Over · {status.pct_used}%</Badge>;
    if (status.pct_used >= 80) return <Badge className="bg-amber-100 text-amber-700 border-amber-300" variant="outline">{status.pct_used}%</Badge>;
    return <Badge variant="outline">{status.pct_used}%</Badge>;
  };

  return (
    <div className="space-y-6 animate-in" data-testid="job-budgets-page">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-lg bg-primary/10"><Banknote className="h-7 w-7 text-primary" /></div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Job Cost vs Budget</h1>
          <p className="text-muted-foreground">Track real cost burn against budget. Threshold alerts route to your portal bell.</p>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="summary-cards">
          <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Total budgeted</p><p className="text-2xl font-bold">${summary.total_budgeted.toFixed(2)}</p></CardContent></Card>
          <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Total spent</p><p className="text-2xl font-bold">${summary.total_spent.toFixed(2)} <span className="text-sm text-muted-foreground">({summary.overall_pct}%)</span></p></CardContent></Card>
          <Card className={summary.over_budget > 0 ? "border-destructive" : ""}><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Over budget</p><p className={`text-2xl font-bold ${summary.over_budget > 0 ? "text-destructive" : ""}`}>{summary.over_budget}</p></CardContent></Card>
          <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Approaching (≥80%)</p><p className="text-2xl font-bold text-amber-700">{summary.approaching_budget}</p></CardContent></Card>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* BUDGETS LIST */}
        <Card>
          <CardHeader className="flex flex-row justify-between items-center">
            <div><CardTitle>{filtered.length} budget{filtered.length === 1 ? "" : "s"}</CardTitle><CardDescription>One per job.</CardDescription></div>
            <div className="flex gap-2 items-center">
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-40" data-testid="filter-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="over">Over budget</SelectItem>
                  <SelectItem value="approaching">Approaching (≥80%)</SelectItem>
                  <SelectItem value="healthy">Healthy</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={() => setBudgetDialog(blankBudget())} data-testid="new-budget-btn"><Plus className="h-4 w-4 mr-1" /> New budget</Button>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Job</TableHead><TableHead>Budget</TableHead><TableHead>Spent</TableHead><TableHead>Status</TableHead><TableHead className="text-right" />
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map((b) => (
                  <TableRow key={b.id} className={`cursor-pointer ${activeBudget?.id === b.id ? "bg-primary/5" : ""} ${b.status?.is_over ? "bg-destructive/5" : ""}`} onClick={() => openBudget(b)} data-testid={`budget-row-${b.id}`}>
                    <TableCell className="font-medium">{schedTitle(b.schedule_id)}</TableCell>
                    <TableCell>${b.total_budget.toFixed(2)}</TableCell>
                    <TableCell>${b.status?.actual_total.toFixed(2)}</TableCell>
                    <TableCell>{statusBadge(b.status)}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setBudgetDialog({ ...b }); }} data-testid={`edit-budget-${b.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={(e) => { e.stopPropagation(); deleteBudget(b.id); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No budgets in this filter.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* COST ENTRIES PANEL */}
        <Card>
          <CardHeader className="flex flex-row justify-between items-center">
            <div>
              <CardTitle>{activeBudget ? `Cost entries — ${schedTitle(activeBudget.schedule_id)}` : "Pick a budget"}</CardTitle>
              <CardDescription>{activeBudget ? `${activeBudget.status?.pct_used}% used · $${activeBudget.status?.remaining.toFixed(2)} remaining` : "Click a budget on the left."}</CardDescription>
            </div>
            {activeBudget && (
              <Button size="sm" onClick={() => setCostDialog(blankCost(activeBudget.schedule_id))} data-testid="add-cost-btn"><Plus className="h-4 w-4 mr-1" /> Log cost</Button>
            )}
          </CardHeader>
          <CardContent>
            {activeBudget ? (
              <>
                <div className="mb-3">
                  <Progress value={Math.min(100, activeBudget.status?.pct_used || 0)} className={activeBudget.status?.is_over ? "[&>div]:bg-destructive" : activeBudget.status?.pct_used >= 80 ? "[&>div]:bg-amber-500" : ""} data-testid="budget-progress" />
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>$0</span>
                    <span>${activeBudget.status?.actual_total.toFixed(2)} of ${activeBudget.total_budget.toFixed(2)}</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                  <div className="border rounded p-2"><p className="text-muted-foreground">Labor</p><p className="font-medium">${activeBudget.status?.actual_labor.toFixed(2)} {activeBudget.status?.labor_pct ? `(${activeBudget.status.labor_pct}%)` : ""}</p></div>
                  <div className="border rounded p-2"><p className="text-muted-foreground">Materials</p><p className="font-medium">${activeBudget.status?.actual_materials.toFixed(2)} {activeBudget.status?.materials_pct ? `(${activeBudget.status.materials_pct}%)` : ""}</p></div>
                  <div className="border rounded p-2"><p className="text-muted-foreground">Other</p><p className="font-medium">${activeBudget.status?.actual_other.toFixed(2)} {activeBudget.status?.other_pct ? `(${activeBudget.status.other_pct}%)` : ""}</p></div>
                </div>
                <Table>
                  <TableHeader><TableRow><TableHead>Kind</TableHead><TableHead>Amount</TableHead><TableHead>Description</TableHead><TableHead>When</TableHead><TableHead /></TableRow></TableHeader>
                  <TableBody>
                    {costEntries.map((c) => (
                      <TableRow key={c.id} data-testid={`cost-row-${c.id}`}>
                        <TableCell><Badge variant="outline">{c.kind}</Badge></TableCell>
                        <TableCell>${c.amount.toFixed(2)}</TableCell>
                        <TableCell className="text-sm">{c.description || "—"}</TableCell>
                        <TableCell className="text-xs">{c.recorded_at && new Date(c.recorded_at).toLocaleDateString()}</TableCell>
                        <TableCell><Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteCost(c.id)}><Trash2 className="h-3.5 w-3.5" /></Button></TableCell>
                      </TableRow>
                    ))}
                    {costEntries.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No cost entries yet.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </>
            ) : <p className="text-muted-foreground py-8 text-center">No budget selected.</p>}
          </CardContent>
        </Card>
      </div>

      {/* Budget editor dialog */}
      <Dialog open={!!budgetDialog} onOpenChange={(o) => !o && setBudgetDialog(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{budgetDialog?.id ? "Edit budget" : "New budget"}</DialogTitle><DialogDescription>Set per-category budgets + alert thresholds.</DialogDescription></DialogHeader>
          {budgetDialog && (
            <div className="space-y-3">
              {!budgetDialog.id && (
                <div>
                  <Label>Job</Label>
                  <Select value={budgetDialog.schedule_id} onValueChange={(v) => setBudgetDialog({ ...budgetDialog, schedule_id: v })}>
                    <SelectTrigger data-testid="budget-job-select"><SelectValue placeholder="Pick a job" /></SelectTrigger>
                    <SelectContent>{schedules.map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-3 gap-2">
                <div><Label>Labor ($)</Label><Input type="number" step="0.01" value={budgetDialog.labor_budget} onChange={(e) => setBudgetDialog({ ...budgetDialog, labor_budget: parseFloat(e.target.value || 0) })} data-testid="labor-budget-input" /></div>
                <div><Label>Materials ($)</Label><Input type="number" step="0.01" value={budgetDialog.materials_budget} onChange={(e) => setBudgetDialog({ ...budgetDialog, materials_budget: parseFloat(e.target.value || 0) })} data-testid="materials-budget-input" /></div>
                <div><Label>Other ($)</Label><Input type="number" step="0.01" value={budgetDialog.other_budget} onChange={(e) => setBudgetDialog({ ...budgetDialog, other_budget: parseFloat(e.target.value || 0) })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Contingency %</Label><Input type="number" step="0.1" value={budgetDialog.contingency_pct} onChange={(e) => setBudgetDialog({ ...budgetDialog, contingency_pct: parseFloat(e.target.value || 0) })} /></div>
                <div>
                  <Label>Alert thresholds (csv %)</Label>
                  <Input value={(budgetDialog.alert_thresholds_pct || []).join(",")} onChange={(e) => {
                    const vals = e.target.value.split(",").map((x) => parseInt(x.trim())).filter((x) => !isNaN(x));
                    setBudgetDialog({ ...budgetDialog, alert_thresholds_pct: vals });
                  }} placeholder="50,80,100" data-testid="thresholds-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <label className="flex items-center gap-2 cursor-pointer"><Switch checked={budgetDialog.notify_admin} onCheckedChange={(v) => setBudgetDialog({ ...budgetDialog, notify_admin: v })} /> Notify admin</label>
                <label className="flex items-center gap-2 cursor-pointer"><Switch checked={budgetDialog.notify_pm} onCheckedChange={(v) => setBudgetDialog({ ...budgetDialog, notify_pm: v })} /> Notify PM/scheduler</label>
              </div>
              <div><Label>Notes</Label><Textarea rows={2} value={budgetDialog.notes || ""} onChange={(e) => setBudgetDialog({ ...budgetDialog, notes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBudgetDialog(null)}>Cancel</Button>
            <Button onClick={saveBudget} data-testid="save-budget-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cost entry dialog */}
      <Dialog open={!!costDialog} onOpenChange={(o) => !o && setCostDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Log cost</DialogTitle><DialogDescription>Adds to the actual spend total + checks thresholds.</DialogDescription></DialogHeader>
          {costDialog && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Kind</Label>
                  <Select value={costDialog.kind} onValueChange={(v) => setCostDialog({ ...costDialog, kind: v })}>
                    <SelectTrigger data-testid="cost-kind-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{COST_KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Amount ($)</Label><Input type="number" step="0.01" value={costDialog.amount} onChange={(e) => setCostDialog({ ...costDialog, amount: parseFloat(e.target.value || 0) })} data-testid="cost-amount-input" /></div>
              </div>
              <div><Label>Description</Label><Textarea rows={2} value={costDialog.description || ""} onChange={(e) => setCostDialog({ ...costDialog, description: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCostDialog(null)}>Cancel</Button>
            <Button onClick={saveCost} data-testid="save-cost-btn">Log</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
